import type { useListRentalListings } from 'src/queries';
import type { CatalogRoute } from 'src/hooks/useCatalogRoute';

import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { FunnelX, CarSimple } from '@phosphor-icons/react';

import { useDayFormat } from 'src/hooks/useDayFormat';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';

import { rentalCardSlots } from 'src/utils/listingCardSlots';

import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/themed/Stack';
import { Pagination } from 'src/components/Pagination';
import { ListingGrid } from 'src/components/ListingGrid';
import { ListingCard } from 'src/components/ListingCard';
import { EmptyState, RequestFailureState } from 'src/components/EmptyState';

interface AvailableRentalsProps {
  route: CatalogRoute;
  /** The page of the rent catalog the screen asked for — one query, read here and for the count line. */
  query: ReturnType<typeof useListRentalListings>;
  /** "Try again" after a failed feed: the screen re-asks the cars and whatever else it shows beside them. */
  onRetry: () => void;
  /** The same after a failed lookup of the make or the model in the address: the lookup, and the lists with it. */
  onRetryLookup: () => void;
}

/** Public browse of available rental listings, a page at a time. Each card opens the booking screen. */
export const AvailableRentals = ({ route, query, onRetry, onRetryLookup }: AvailableRentalsProps) => {
  const { data, isRefetching, isPlaceholderData } = query;
  const items = useMemo(() => data?.items ?? [], [data?.items]);
  // "No cars available to rent yet" is a statement about the market, and it must not be made
  // when the question never reached the api (BUG-06): a query without a network is paused, not
  // failed, and it used to land in the empty branch below.
  const state = useQueryScreenState(query);
  const dayOf = useDayFormat();
  const reset = () => route.navigateTo({ makeSlug: undefined, modelSlug: undefined, filters: {} });

  if (!route.ready) {
    return route.lookupState === 'loading' || route.lookupState === 'ready' ? (
      <Loader />
    ) : (
      <RequestFailureState kind={route.lookupState} onRetry={onRetryLookup} />
    );
  }

  if (route.unknownSlug || (state === 'ready' && isEmpty(items) && route.filtered)) {
    return (
      <EmptyState
        icon={<FunnelX size={48} />}
        title="No cars match these filters"
        description="Try other filters, or reset them."
        action={{ label: 'Reset filters', onClick: reset }}
      />
    );
  }

  // A page link keeps the previous page on screen while the next one loads; only a refresh spins.
  if (state === 'loading' || (isRefetching && !isPlaceholderData)) {
    return <Loader />;
  }

  if (state !== 'ready') {
    return <RequestFailureState kind={state} onRetry={onRetry} />;
  }

  if (isEmpty(items)) {
    return (
      <EmptyState
        icon={<CarSimple size={48} />}
        title="No cars available to rent yet"
        description="Dealers are adding cars. Check back soon."
      />
    );
  }

  return (
    <Stack width="100%" gap={4}>
      <ListingGrid>
        {items.map((listing) => (
          <ListingCard key={listing.id} {...rentalCardSlots(listing, dayOf)} />
        ))}
      </ListingGrid>
      <Pagination total={data?.total ?? 0} page={route.page} hrefFor={(page) => route.pathTo({ page })} />
    </Stack>
  );
};
